
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';

interface Activity {
  id: string;
  type: 'member_joined' | 'member_left' | 'post_created' | 'role_changed' | 'group_updated';
  userId: string;
  username: string;
  avatarUrl: string | null;
  description: string;
  metadata?: {
    postTitle?: string;
    postId?: string;
    newRole?: string;
    oldRole?: string;
  };
  createdAt: string;
}

interface GroupActivityFeedProps {
  groupId: string;
  members: {
    user_id: string;
    role: string;
    joined_at: string;
    user_profiles: {
      username: string;
      avatar_url: string;
    };
  }[];
  posts: {
    id: string;
    title: string;
    author_id: string;
    created_at: string;
    user_profiles: {
      username: string;
      avatar_url: string;
    };
  }[];
}

const GroupActivityFeed = ({ groupId, members, posts }: GroupActivityFeedProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    generateActivities();
  }, [members, posts]);

  const generateActivities = () => {
    setLoading(true);
    
    const allActivities: Activity[] = [];

    // Add member join activities
    members.forEach((member) => {
      allActivities.push({
        id: `join-${member.user_id}`,
        type: 'member_joined',
        userId: member.user_id,
        username: member.user_profiles?.username || 'Unknown',
        avatarUrl: member.user_profiles?.avatar_url || null,
        description: 'joined the group',
        createdAt: member.joined_at,
      });

      // Add role change activities for admins/moderators
      if (member.role === 'admin' || member.role === 'moderator') {
        allActivities.push({
          id: `role-${member.user_id}`,
          type: 'role_changed',
          userId: member.user_id,
          username: member.user_profiles?.username || 'Unknown',
          avatarUrl: member.user_profiles?.avatar_url || null,
          description: `was promoted to ${member.role}`,
          metadata: { newRole: member.role },
          createdAt: member.joined_at,
        });
      }
    });

    // Add post creation activities
    posts.forEach((post) => {
      allActivities.push({
        id: `post-${post.id}`,
        type: 'post_created',
        userId: post.author_id,
        username: post.user_profiles?.username || 'Unknown',
        avatarUrl: post.user_profiles?.avatar_url || null,
        description: 'created a new discussion',
        metadata: { postTitle: post.title, postId: post.id },
        createdAt: post.created_at,
      });
    });

    // Sort by date (newest first)
    allActivities.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setActivities(allActivities);
    setLoading(false);
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'member_joined':
        return { icon: 'ri-user-add-line', color: 'text-emerald-500', bg: 'bg-emerald-50' };
      case 'member_left':
        return { icon: 'ri-user-unfollow-line', color: 'text-red-500', bg: 'bg-red-50' };
      case 'post_created':
        return { icon: 'ri-article-line', color: 'text-teal-500', bg: 'bg-teal-50' };
      case 'role_changed':
        return { icon: 'ri-shield-star-line', color: 'text-amber-500', bg: 'bg-amber-50' };
      case 'group_updated':
        return { icon: 'ri-settings-3-line', color: 'text-indigo-500', bg: 'bg-indigo-50' };
      default:
        return { icon: 'ri-notification-line', color: 'text-gray-500', bg: 'bg-gray-50' };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const displayedActivities = showAll ? activities : activities.slice(0, 8);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-pulse-line text-teal-500"></i>
          </div>
          <h3 className="font-semibold text-gray-900">Group Activity</h3>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-pulse-line text-teal-500"></i>
          </div>
          <h3 className="font-semibold text-gray-900">Group Activity</h3>
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
          {activities.length} activities
        </span>
      </div>

      <div className="divide-y divide-gray-50">
        {displayedActivities.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-time-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm">No activity yet</p>
            <p className="text-gray-400 text-xs mt-1">Activities will appear here as members engage</p>
          </div>
        ) : (
          displayedActivities.map((activity) => {
            const iconStyle = getActivityIcon(activity.type);
            return (
              <div key={activity.id} className="px-5 py-3 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full ${iconStyle.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${iconStyle.icon} ${iconStyle.color} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link 
                        to={`/user/${activity.userId}`}
                        className="font-medium text-gray-900 hover:text-teal-600 transition-colors text-sm"
                      >
                        {activity.username}
                      </Link>
                      <span className="text-gray-600 text-sm">{activity.description}</span>
                    </div>
                    {activity.metadata?.postTitle && (
                      <p className="text-sm text-gray-500 mt-0.5 truncate">
                        "{activity.metadata.postTitle}"
                      </p>
                    )}
                    <span className="text-xs text-gray-400 mt-1 block">
                      {formatTimeAgo(activity.createdAt)}
                    </span>
                  </div>
                  {activity.type === 'role_changed' && activity.metadata?.newRole && (
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      activity.metadata.newRole === 'admin' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-teal-100 text-teal-700'
                    }`}>
                      {activity.metadata.newRole}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {activities.length > 8 && (
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-center text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer flex items-center justify-center gap-1"
          >
            {showAll ? (
              <>
                <i className="ri-arrow-up-s-line"></i>
                Show Less
              </>
            ) : (
              <>
                <i className="ri-arrow-down-s-line"></i>
                View All {activities.length} Activities
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupActivityFeed;
